import React, { useState } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import CallButton from '../CallButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Edit2, ScanLine, Loader2, X, FileText, ChevronDown, ChevronRight,
  Upload, Download, Trash2, RefreshCw, MessageCircle, Phone, History
} from 'lucide-react';
import { DOC_TYPES, getStatoStyle } from './constants';

function InfoRow({ label, value, className = '' }) {
  return (
    <div className="flex justify-between py-1 border-b border-slate-100">
      <span className="text-slate-400 text-xs">{label}</span>
      <span className={`text-slate-700 text-xs font-medium ${className}`}>{value}</span>
    </div>
  );
}

export const PraticaDetail = ({ pratica, isAdmin, onUpload, onOCR, uploading, ocrLoading, onEdit, onApplyOCR, onDeleteDoc }) => {
  const [uploadType, setUploadType] = useState('contratto');
  const [uploadScadenza, setUploadScadenza] = useState('');
  const [ocrResult, setOcrResult] = useState(null);
  const [docSection, setDocSection] = useState(true);
  const fileInputRef = React.useRef(null);
  const ocrInputRef = React.useRef(null);
  const [ocrType, setOcrType] = useState('contratto');

  const statoStyle = getStatoStyle(pratica.stato);
  const token = localStorage.getItem('token');
  const downloadUrl = (docId) => `${process.env.REACT_APP_BACKEND_URL}/api/prestiti/${pratica.id}/documenti/${docId}/download?token=${encodeURIComponent(token)}`;

  return (
    <>
      <DialogHeader>
        <div className="flex items-center justify-between">
          <div>
            <DialogTitle className="text-lg">{pratica.nome_cliente} {pratica.cognome_cliente}</DialogTitle>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[10px]">{pratica.tipo}</Badge>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statoStyle.color}`}>{statoStyle.label}</span>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={onEdit} className="text-xs"><Edit2 size={13} className="mr-1" /> Modifica</Button>
        </div>
      </DialogHeader>

      <div className="space-y-5 mt-3">
        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {pratica.codice_fiscale && <InfoRow label="Codice Fiscale" value={pratica.codice_fiscale} />}
          {pratica.data_nascita && <InfoRow label="Data Nascita" value={pratica.data_nascita} />}
          {pratica.luogo_nascita && <InfoRow label="Luogo Nascita" value={pratica.luogo_nascita} />}
          {pratica.telefono && <InfoRow label="Telefono" value={pratica.telefono} />}
          {pratica.email && <InfoRow label="Email" value={pratica.email} />}
          {pratica.indirizzo_residenza && <InfoRow label="Indirizzo" value={pratica.indirizzo_residenza} />}
          {pratica.datore_lavoro && <InfoRow label="Datore di Lavoro" value={pratica.datore_lavoro} />}
          {pratica.compagnia && <InfoRow label="Compagnia" value={pratica.compagnia} />}
          {pratica.importo_richiesto && <InfoRow label="Importo Richiesto" value={`${Number(pratica.importo_richiesto).toLocaleString('it-IT')} EUR`} />}
          {pratica.importo_erogato && <InfoRow label="Importo Erogato" value={`${Number(pratica.importo_erogato).toLocaleString('it-IT')} EUR`} />}
          {pratica.durata_mesi && <InfoRow label="Durata" value={`${pratica.durata_mesi} mesi`} />}
          {pratica.rata_mensile && <InfoRow label="Rata Mensile" value={`${Number(pratica.rata_mensile).toLocaleString('it-IT')} EUR`} />}
          {pratica.tasso_tan && <InfoRow label="TAN" value={`${pratica.tasso_tan}%`} />}
          {pratica.tasso_taeg && <InfoRow label="TAEG" value={`${pratica.tasso_taeg}%`} />}
          {pratica.assicurazione !== undefined && <InfoRow label="Assicurazione" value={pratica.assicurazione ? 'Si' : 'No'} />}
          {pratica.data_stipula && <InfoRow label="Data Stipula" value={pratica.data_stipula} />}
          {pratica.data_scadenza && <InfoRow label="Data Scadenza" value={pratica.data_scadenza} />}
          {pratica.data_erogazione && <InfoRow label="Data Erogazione" value={pratica.data_erogazione} />}
          {pratica.segnalato_da && <InfoRow label="Segnalato da" value={pratica.segnalato_da} />}
          {pratica.numero_contratto && <InfoRow label="N. Contratto" value={pratica.numero_contratto} />}
          {isAdmin && pratica.provvigione_importo && <InfoRow label="Provvigione" value={`${Number(pratica.provvigione_importo).toLocaleString('it-IT')} EUR`} className="text-green-700 font-semibold" />}
        </div>
        {pratica.note && <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">{pratica.note}</div>}

        {/* ─── Rinnovo CQ Section ─── */}
        {pratica.rinnovo && (
          <div className={`border-2 rounded-xl p-4 ${
            pratica.rinnovo.rinnovo_disponibile
              ? 'border-green-300 bg-green-50/30'
              : pratica.rinnovo.mesi_mancanti <= 6
                ? 'border-amber-200 bg-amber-50/30'
                : 'border-slate-200 bg-slate-50/30'
          }`} data-testid="rinnovo-detail-section">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2">
              <RefreshCw size={16} className={pratica.rinnovo.rinnovo_disponibile ? 'text-green-600' : 'text-slate-400'} />
              Stato Rinnovo
              {pratica.rinnovo.rinnovo_disponibile && (
                <Badge className="bg-green-100 text-green-700 text-[10px] ml-1">RINNOVABILE</Badge>
              )}
            </h3>
            <p className="text-xs text-slate-600 mb-2">{pratica.rinnovo.rinnovo_info}</p>
            {pratica.rinnovo.rate_necessarie > 0 && (
              <div className="mb-3">
                <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                  <span>Rate pagate: {pratica.rinnovo.rate_pagate}/{pratica.rinnovo.rate_totali}</span>
                  <span>Data rinnovo: {pratica.rinnovo.rinnovo_data}</span>
                </div>
                <div className="h-2.5 bg-white rounded-full overflow-hidden border">
                  <div className={`h-full rounded-full ${pratica.rinnovo.rinnovo_disponibile ? 'bg-green-500' : 'bg-amber-400'}`}
                    style={{ width: `${Math.min(100, (pratica.rinnovo.rate_pagate / pratica.rinnovo.rate_necessarie) * 100)}%` }} />
                </div>
              </div>
            )}
            {pratica.rinnovo.rinnovo_disponibile && pratica.telefono && (
              <div className="flex gap-2">
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs" onClick={() => {
                  api.post(`/prestiti/${pratica.id}/contatta-rinnovo?metodo=whatsapp`).then(() => toast.success('Messaggio rinnovo inviato!')).catch(e => toast.error(e.response?.data?.detail || 'Errore'));
                }} data-testid="detail-rinnovo-wa"><MessageCircle size={13} className="mr-1" /> Contatta via WhatsApp</Button>
                <CallButton number={pratica.telefono} description={`${pratica.nome_cliente} ${pratica.cognome_cliente}`} variant="button" className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium h-8 px-3 border border-slate-200 hover:bg-slate-50"><Phone size={13} className="mr-1" /> Chiama</CallButton>
              </div>
            )}
            {/* Conteggio Estintivo nel dettaglio */}
            {pratica.rinnovo.conteggio_estintivo && (
              <div className="bg-white border border-slate-200 rounded-lg p-3 mt-2">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Conteggio Estintivo Stimato</p>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-[10px] text-slate-400">Lordo Totale</p>
                    <p className="text-lg font-bold text-slate-700">{Number(pratica.rinnovo.conteggio_estintivo.lordo_totale).toLocaleString('it-IT', {minimumFractionDigits: 2})} EUR</p>
                    <p className="text-[9px] text-slate-400">{pratica.rinnovo.conteggio_estintivo.rate_restanti} rate restanti × rata</p>
                  </div>
                  <div className="bg-rose-50 rounded-lg p-2">
                    <p className="text-[10px] text-rose-500">Netto Estinzione</p>
                    <p className="text-lg font-bold text-rose-700">{Number(pratica.rinnovo.conteggio_estintivo.saldo_estinzione).toLocaleString('it-IT', {minimumFractionDigits: 2})} EUR</p>
                    <p className="text-[9px] text-slate-400">capitale residuo + spese {pratica.rinnovo.conteggio_estintivo.perc_spese || 1}%</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center mt-2">
                  <div>
                    <p className="text-[10px] text-slate-400">Spese Estinzione</p>
                    <p className="text-sm font-semibold text-slate-600">{Number(pratica.rinnovo.conteggio_estintivo.spese_estinzione).toLocaleString('it-IT', {minimumFractionDigits: 2})} EUR</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-1.5">
                    <p className="text-[10px] text-green-600">Risparmio Interessi</p>
                    <p className="text-sm font-bold text-green-700">{Number(pratica.rinnovo.conteggio_estintivo.risparmio_interessi || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})} EUR</p>
                  </div>
                </div>
                <p className="text-[9px] text-slate-400 mt-2 text-right">{pratica.rinnovo.conteggio_estintivo.metodo}</p>
              </div>
            )}
          </div>
        )}

        {/* ─── OCR Section ─── */}
        <div className="border border-amber-200 rounded-xl p-4 bg-amber-50/30">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
            <ScanLine size={16} className="text-amber-600" /> Lettura OCR Documento
          </h3>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="min-w-[140px]">
              <Label className="text-xs text-slate-500">Tipo documento</Label>
              <select value={ocrType} onChange={e => setOcrType(e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white">
                {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <input type="file" ref={ocrInputRef} accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={async e => {
              if (e.target.files?.[0]) {
                const result = await onOCR(pratica.id, e.target.files[0], ocrType);
                if (result && !result.error) setOcrResult(result);
                else if (result?.error) toast.error(result.error);
              }
            }} />
            <Button size="sm" variant="outline" onClick={() => ocrInputRef.current?.click()} disabled={ocrLoading} className="text-xs" data-testid="ocr-btn">
              {ocrLoading ? <Loader2 size={13} className="animate-spin mr-1" /> : <ScanLine size={13} className="mr-1" />}
              Analizza con AI
            </Button>
          </div>
          {ocrResult && (
            <div className="mt-3 bg-white rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-slate-600">Dati Estratti</h4>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="text-[10px] h-6" onClick={() => { onApplyOCR(ocrResult); setOcrResult(null); }} data-testid="apply-ocr-btn">Applica alla pratica</Button>
                  <button onClick={() => setOcrResult(null)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                {Object.entries(ocrResult).filter(([_, v]) => v && v !== '').map(([k, v]) => (
                  <div key={k} className="flex gap-1"><span className="text-slate-400 font-medium">{k}:</span><span className="text-slate-700">{v}</span></div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ─── Documents Section ─── */}
        <div>
          <button onClick={() => setDocSection(!docSection)} className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-800">
            {docSection ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <FileText size={16} className="text-slate-500" />
            Documenti ({pratica.documenti?.length || 0})
          </button>
          {docSection && (
            <>
              {/* Upload */}
              <div className="flex gap-2 items-end mb-3 flex-wrap">
                <div className="min-w-[120px]">
                  <Label className="text-xs text-slate-500">Tipo</Label>
                  <select value={uploadType} onChange={e => setUploadType(e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white">
                    {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div className="min-w-[120px]">
                  <Label className="text-xs text-slate-500">Scadenza doc.</Label>
                  <Input type="date" value={uploadScadenza} onChange={e => setUploadScadenza(e.target.value)} className="mt-1 text-xs h-8" />
                </div>
                <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={async e => {
                  if (e.target.files?.[0]) await onUpload(pratica.id, e.target.files[0], uploadType, uploadScadenza);
                }} />
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="text-xs" data-testid="upload-doc-btn">
                  {uploading ? <Loader2 size={13} className="animate-spin mr-1" /> : <Upload size={13} className="mr-1" />}
                  Carica
                </Button>
              </div>
              {/* Doc list */}
              <div className="space-y-1.5">
                {(!pratica.documenti || pratica.documenti.length === 0) ? (
                  <p className="text-xs text-slate-400 py-4 text-center">Nessun documento caricato</p>
                ) : pratica.documenti.map(d => (
                  <div key={d.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg" data-testid={`doc-${d.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={14} className="text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-700 truncate">{d.nome_file}</p>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400">
                          <span>{DOC_TYPES.find(t => t.value === d.tipo)?.label || d.tipo}</span>
                          {d.data_scadenza && <span>Scade: {d.data_scadenza}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-0.5">
                      <a href={downloadUrl(d.id)} download={d.nome_file}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Scarica" data-testid={`download-${d.id}`}><Download size={13} /></a>
                      <button onClick={() => onDeleteDoc(d.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title="Elimina"><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
